import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaLock, FaInfoCircle, FaUserCircle } from "react-icons/fa";
import { useAccount, usePublicClient } from "wagmi";
import { useX402Pay } from "../../hooks/useX402Pay";
import { useDirectPay } from "../../hooks/useDirectPay";
import { useHorusPay } from "../../hooks/useHorusPay";
import { useAuth } from "../web3/context/AuthContext";
import { getTxExplorerUrl } from "../../helper/x402/chains";
import { ConnectWalletCTA } from "./ConnectWalletCTA";
import { ConnectedWalletRow } from "./ConnectedWalletRow";
import { HorusLoginButton } from "./HorusLoginButton";
import { MethodSwitcher } from "./MethodSwitcher";
import { WalletSourceSwitcher } from "./WalletSourceSwitcher";
import { PayStatusStrip } from "./PayStatusStrip";

/**
 * Single-flow checkout with a two-level choice:
 *
 *   walletSource:  'external' | 'horus'
 *     └─ method:   'x402' | 'direct'    (only when walletSource === 'external';
 *                                         Horus is always direct-style since the
 *                                         backend can't sign EIP-712 yet)
 *
 * The top <WalletSourceSwitcher /> picks where the wallet comes
 * from. The sub-flow below it renders accordingly:
 *
 *   external → connect via wagmi → x402 / direct (MethodSwitcher)
 *   horus    → log in via Horus account → custodial direct transfer
 *
 * Both sub-flows converge on the same receipt handoff —
 * `/pay/receipt/:txHash` with a standardized receipt payload.
 *
 * Rules-of-hooks: all three pay hooks (x402 / direct / horus) run
 * unconditionally every render. Each starts at `idle` and only
 * advances when its own pay() / payNow() fires, so there's no
 * cross-talk from calling a hook we aren't using.
 */
export function CheckoutPanel({ link }) {
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const publicClient = usePublicClient({
    chainId: link?.chainId ? Number(link.chainId) : undefined,
  });
  const {
    isLoggedIn,
    loginOption,
    email,
    entityInfo,
    logout,
  } = useAuth();

  // ───────────────────────────────────────────────────────────
  // Wallet source + method state
  // ───────────────────────────────────────────────────────────

  // `horus` is only usable when the user is either (a) logged in
  // via a custodial account, or (b) logged out entirely (can opt
  // into login). Non-custodial logged-in users — i.e. already
  // using an external wallet — don't get the Horus option; it
  // would be redundant. Native-token links exclude Horus: the
  // custodial pipeline only supports ERC-20.
  const canUseHorus =
    !link?.isNative &&
    (!isLoggedIn || loginOption === "custodial");

  // External wallet is always offerable — wagmi can connect on
  // demand even if nobody is connected yet.
  const canUseExternal = true;

  const [walletSource, setWalletSource] = useState(null); // 'external' | 'horus'
  const [userPickedSource, setUserPickedSource] = useState(false);

  // Default wallet source is ALWAYS Horus when the link supports
  // it — feature our own wallet as the primary path, regardless
  // of whether the visitor is already logged in. Logged-out
  // payers get nudged toward the login modal; they can still
  // one-click over to "Connect Wallet" if they prefer an
  // external signer. Horus is only unavailable for native-token
  // links (the custodial pipeline is ERC-20 only) or when the
  // viewer is already non_custodial-logged-in via an external
  // wallet (redundant with Connect Wallet) — handled by the
  // `canUseHorus` guard computed above.
  //
  // Locked once the user manually flips via WalletSourceSwitcher.
  useEffect(() => {
    if (walletSource !== null && userPickedSource) return;
    const next = canUseHorus ? "horus" : "external";
    setWalletSource(next);
  }, [canUseHorus, walletSource, userPickedSource]);

  // Smart-account detection — relevant only for the external
  // path (Horus doesn't care, since it doesn't use EIP-3009). If
  // the connected EOA has code attached (EIP-7702, 4337, Safe,
  // etc.) x402 will fail on USDC; auto-flip the default method to
  // direct so those payers land on a flow that works.
  const [isSmartAccount, setIsSmartAccount] = useState(false);
  useEffect(() => {
    if (!address || !publicClient) {
      setIsSmartAccount(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const code = await publicClient.getBytecode({ address });
        if (!cancelled) setIsSmartAccount(Boolean(code) && code !== "0x");
      } catch {
        if (!cancelled) setIsSmartAccount(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [address, publicClient]);

  // Payment method (x402 / direct) — only meaningful within the
  // external-wallet flow. Horus always uses direct-style settlement.
  const methodsFromBackend = link?.paymentMethods || [];
  const [activeMethod, setActiveMethod] = useState(null);
  const [userPickedMethod, setUserPickedMethod] = useState(false);
  useEffect(() => {
    if (!methodsFromBackend.length) return;
    if (userPickedMethod) return;
    const recommended = link?.recommendedMethod;
    const defaultForEoa = methodsFromBackend.includes(recommended)
      ? recommended
      : methodsFromBackend[0];
    let preferred = defaultForEoa;
    if (isSmartAccount && methodsFromBackend.includes("direct")) {
      preferred = "direct";
    }
    setActiveMethod(preferred);
  }, [link, methodsFromBackend, isSmartAccount, userPickedMethod]);

  // ───────────────────────────────────────────────────────────
  // Pay hooks
  // ───────────────────────────────────────────────────────────

  const x402 = useX402Pay(link);
  const direct = useDirectPay(link);
  const horus = useHorusPay(link);

  const handleMethodSwitch = (next) => {
    if (activeMethod === "x402") x402.reset();
    else if (activeMethod === "direct") direct.reset();
    setActiveMethod(next);
    setUserPickedMethod(true);
  };

  const handleSourceSwitch = (next) => {
    // Reset both side's pay state so the previous source's
    // "error" / "signing" flag doesn't leak across.
    x402.reset();
    direct.reset();
    horus.reset();
    setWalletSource(next);
    setUserPickedSource(true);
  };

  const handleChangeWallet = () => {
    x402.reset();
    direct.reset();
    horus.reset();
  };

  // Success handoff — the same for all three hooks; the receipt
  // page accepts the normalized payload. `replace: true` keeps
  // the pay URL out of back-button history.
  useEffect(() => {
    let receipt = null;
    let method = null;
    if (x402.status === "success" && x402.receipt) {
      receipt = x402.receipt;
      method = "x402";
    } else if (direct.phase === "success" && direct.receipt) {
      receipt = { receipt: direct.receipt };
      method = "direct";
    } else if (horus.phase === "success" && horus.receipt) {
      receipt = { receipt: horus.receipt };
      method = "horus";
    }
    if (!receipt) return;
    const txHash =
      receipt?.receipt?.tx_hash || receipt?.receipt?.txHash;
    if (!txHash) return;
    try {
      sessionStorage.setItem(
        `pay:receipt:${txHash}`,
        JSON.stringify({ receipt, link, method }),
      );
    } catch {
      // Storage blocked — in-memory handoff still works.
    }
    navigate(`/pay/receipt/${txHash}`, {
      state: { receipt, link, method },
      replace: true,
    });
  }, [
    x402.status,
    x402.receipt,
    direct.phase,
    direct.receipt,
    horus.phase,
    horus.receipt,
    link,
    navigate,
  ]);

  // ───────────────────────────────────────────────────────────
  // Early-return rendering paths
  // ───────────────────────────────────────────────────────────

  if (!methodsFromBackend.length) {
    return (
      <div className="pay-checkout">
        <h3 className="pay-checkout__heading">
          This link isn't accepting payments
        </h3>
        <p className="pay-panel__subheading">
          The merchant hasn't configured a supported payment method. Contact
          them for an alternate link.
        </p>
      </div>
    );
  }

  // Brief placeholder while the success-navigate effect fires.
  if (
    (x402.status === "success" && x402.receipt) ||
    (direct.phase === "success" && direct.receipt) ||
    (horus.phase === "success" && horus.receipt)
  ) {
    return (
      <div className="pay-checkout">
        <h3 className="pay-checkout__heading">
          Payment complete — loading receipt…
        </h3>
      </div>
    );
  }

  const sourceSwitcher = (
    <WalletSourceSwitcher
      activeSource={walletSource}
      canUseHorus={canUseHorus}
      canUseExternal={canUseExternal}
      onSwitch={handleSourceSwitch}
    />
  );

  // ───────────────────────────────────────────────────────────
  // Horus (custodial) flow
  // ───────────────────────────────────────────────────────────

  if (walletSource === "horus") {
    if (!isLoggedIn) {
      return (
        <div className="pay-checkout">
          {sourceSwitcher}
          <h3 className="pay-checkout__heading">Pay with Horus Wallet</h3>
          <p className="pay-panel__subheading">
            Log in to pay {link.amountDisplay} straight from your Horus
            Wallet balance — no external wallet needed.
          </p>
          <HorusLoginButton />
        </div>
      );
    }

    const rawH = horus.phase;
    const phaseH = rawH === "success" ? "idle" : rawH || "idle";
    const isWorkingH = phaseH !== "idle" && phaseH !== "error";

    let labelH;
    if (phaseH === "idle" || phaseH === "error") {
      labelH = `Pay ${link.amountDisplay}`;
    } else if (phaseH === "password") {
      labelH = "Enter password…";
    } else if (phaseH === "signing") {
      labelH = "Signing with Horus Wallet…";
    } else {
      labelH = `Pay ${link.amountDisplay}`;
    }

    // "Change account" on the Horus path means "log out of the
    // current Horus account so I can sign in with a different
    // one". Resetting pay state alone wouldn't surface the login
    // modal again — full logout flips AuthContext.isLoggedIn and
    // the CheckoutPanel then re-renders with HorusLoginButton.
    const handleChangeHorusAccount = async () => {
      horus.reset();
      try {
        await logout();
      } catch {
        // Logout is best-effort; if Firebase signOut fails the
        // user can still try again from the header.
      }
    };

    return (
      <div className="pay-checkout">
        {sourceSwitcher}
        <h3 className="pay-checkout__heading">Review your payment</h3>
        <HorusAccountRow
          email={email}
          address={entityInfo?.activePublicKey}
          onChange={handleChangeHorusAccount}
        />

        <button
          type="button"
          className="pay-button"
          onClick={() => {
            if (phaseH === "error") horus.reset();
            horus.pay();
          }}
          disabled={isWorkingH}
        >
          <FaLock size={14} />
          {labelH}
        </button>

        <PayStatusStrip
          phase={phaseH}
          error={horus.error}
          network={link.network}
        />
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────
  // External-wallet flow (existing behavior: x402 / direct)
  // ───────────────────────────────────────────────────────────

  if (!isConnected) {
    const subcopy =
      activeMethod === "direct"
        ? `Pay with direct transfer on ${link.network}. Your wallet covers the on-chain gas fee.`
        : `Pay gasless with ${link.tokenSymbol} on ${link.network}. You'll sign a single authorization — no gas, no approval transaction.`;
    return (
      <div className="pay-checkout">
        {sourceSwitcher}
        <h3 className="pay-checkout__heading">Connect your wallet</h3>
        <p className="pay-panel__subheading">{subcopy}</p>
        <ConnectWalletCTA />
        <MethodSwitcher
          link={link}
          activeMethod={activeMethod}
          availableMethods={methodsFromBackend}
          onSwitch={handleMethodSwitch}
        />
      </div>
    );
  }

  const isX402 = activeMethod === "x402";
  const raw = isX402 ? x402.status : direct.phase;
  const phase = raw === "success" ? "idle" : raw || "idle";
  const error = isX402 ? x402.error : direct.error;
  const isWorking = phase !== "idle" && phase !== "error";

  const explorerUrl =
    !isX402 && direct.txHash
      ? getTxExplorerUrl(link.chainId, direct.txHash)
      : null;

  let buttonLabel;
  if (phase === "idle" || phase === "error") {
    buttonLabel = `Pay ${link.amountDisplay}`;
  } else if (phase === "switching") {
    buttonLabel = "Switching network…";
  } else if (phase === "signing") {
    buttonLabel = "Sign in wallet…";
  } else if (phase === "submitting") {
    buttonLabel = "Settling on-chain…";
  } else if (phase === "sending") {
    buttonLabel = "Step 1 of 2 — Confirm in wallet…";
  } else if (phase === "attesting") {
    buttonLabel = "Step 2 of 2 — Sign confirmation…";
  } else if (phase === "confirming") {
    buttonLabel = direct.requiredConfirmations
      ? `Confirming (${direct.confirmations}/${direct.requiredConfirmations})…`
      : "Waiting for confirmations…";
  } else if (phase === "recording") {
    buttonLabel = "Recording payment…";
  } else {
    buttonLabel = `Pay ${link.amountDisplay}`;
  }

  const handlePay = () => {
    if (phase === "error") {
      if (isX402) x402.reset();
      else direct.reset();
    }
    if (isX402) x402.payNow();
    else direct.pay();
  };

  return (
    <div className="pay-checkout">
      {sourceSwitcher}
      <h3 className="pay-checkout__heading">Review your payment</h3>
      <ConnectedWalletRow onChange={handleChangeWallet} />

      {isSmartAccount && activeMethod === "direct" && !userPickedMethod && (
        <div className="pay-smart-account-note">
          <FaInfoCircle size={12} />
          <span>
            Your wallet is a smart account, which USDC's gasless (x402)
            flow doesn't support. Defaulting to direct transfer — you'll
            pay the on-chain gas fee.
          </span>
        </div>
      )}

      <button
        type="button"
        className="pay-button"
        onClick={handlePay}
        disabled={isWorking}
      >
        <FaLock size={14} />
        {buttonLabel}
      </button>

      <PayStatusStrip
        phase={phase}
        error={error}
        network={link.network}
        confirmations={direct.confirmations}
        requiredConfirmations={direct.requiredConfirmations}
        explorerUrl={explorerUrl}
      />

      <MethodSwitcher
        link={link}
        activeMethod={activeMethod}
        availableMethods={methodsFromBackend}
        onSwitch={handleMethodSwitch}
      />
    </div>
  );
}

/**
 * Identity row for the Horus path — parallels ConnectedWalletRow
 * but sources the identity from AuthContext (email + activePublicKey)
 * instead of wagmi.
 */
function HorusAccountRow({ email, address, onChange }) {
  const short =
    address && address.length > 10
      ? `${address.slice(0, 6)}…${address.slice(-4)}`
      : address;
  return (
    <div className="pay-connected-row">
      <span className="pay-horus-identity">
        <FaUserCircle size={16} />
        <span>
          Paying as <strong>{email || "your Horus account"}</strong>
          {short && (
            <span className="pay-horus-identity__addr"> · {short}</span>
          )}
        </span>
      </span>
      <button
        type="button"
        className="pay-connected-row__change"
        onClick={onChange}
      >
        Change
      </button>
    </div>
  );
}
